// eslint-disable-next-line no-redeclare, @typescript-eslint/no-redeclare
import { Divider, Group, NumberInput, Stack, Switch, Text } from '@mantine/core';
import { FC, useState } from 'react';
import type { ClipboardEvent as ReactClipboardEvent, KeyboardEvent as ReactKeyboardEvent } from 'react';
import ConfigOption from '../../../Atoms/ConfigOption';
import LinkText from '../../../Atoms/LinkText';
import { DocumentType as BookitupDocumentType, PositionDto } from '../../../Service/DocumentT';

interface Props {
  position: PositionDto;
  documentType?: BookitupDocumentType;
  hasFlowLicence: boolean;
  onChange: (patch: Partial<PositionDto>) => Promise<boolean>;
}

const handleIntegerKeyDown = (keyEvent: ReactKeyboardEvent<HTMLInputElement>) => {
  const navigationKeys = ['Backspace', 'Delete', 'Tab', 'Enter', 'Escape', 'ArrowLeft', 'ArrowRight', 'Home', 'End'];

  if (
    keyEvent.metaKey ||
    keyEvent.ctrlKey ||
    keyEvent.altKey ||
    navigationKeys.includes(keyEvent.key) ||
    /^\d$/.test(keyEvent.key)
  ) {
    return;
  }

  keyEvent.preventDefault();
};

const handleIntegerPaste = (pasteEvent: ReactClipboardEvent<HTMLInputElement>) => {
  if (!/^\d+$/.test(pasteEvent.clipboardData.getData('text'))) {
    pasteEvent.preventDefault();
  }
};

const PositionSettings: FC<Props> = ({ position, documentType, hasFlowLicence, onChange }) => {
  const [showMoreSettings, setShowMoreSettings] = useState(false);
  const { type: positionKind, optional, autoSelect, negotiable, min, max } = position;
  const isFreeTextPosition = positionKind === 'FREE_TEXT';
  const isPartialInvoice = documentType === 'PARTIAL_INVOICE';
  const showTypeSetting = !isPartialInvoice && hasFlowLicence;
  const showPortalSettings = !isPartialInvoice && documentType === 'OFFER' && !isFreeTextPosition;
  const showNegotiableSetting = showPortalSettings && hasFlowLicence;
  const showOptionalSetting = showPortalSettings && (showMoreSettings || optional);
  const showAutoSelectSetting = showPortalSettings && optional && (showMoreSettings || autoSelect);
  const showNegotiableControl = showNegotiableSetting && (showMoreSettings || negotiable);
  const hasSettings = showTypeSetting || showPortalSettings;

  if (!hasSettings) {
    return null;
  }

  return (
    <>
      <Divider label="Einstellungen" labelPosition="center" />
      <Stack mt="xs" px="xs" spacing="xs">
        {showTypeSetting && (
          <ConfigOption
            title="Freitext"
            description="Zeigt nur Beschreibung und Unterbeschreibung an und blendet Preise, Mengen, Ressourcen und Portaloptionen aus."
            rightSection={
              <Switch
                size="sm"
                aria-label="Freitext"
                checked={isFreeTextPosition}
                onChange={() => onChange({ type: isFreeTextPosition ? 'REGULAR' : 'FREE_TEXT' })}
              />
            }
          />
        )}
        {showPortalSettings && (
          <>
            {(showOptionalSetting || showAutoSelectSetting || showNegotiableControl) && (
              <Stack spacing="xs">
                {showOptionalSetting && (
                  <ConfigOption
                    title="Optionale Position"
                    description="Kunden können diese Position im Portal auswählen oder abwählen."
                    rightSection={
                      <Switch
                        size="sm"
                        aria-label="Optionale Position"
                        checked={optional}
                        onChange={() => onChange({ optional: !optional })}
                      />
                    }
                  />
                )}
                {showAutoSelectSetting && (
                  <ConfigOption
                    title="Option vorausgewählt"
                    description="Diese optionale Position ist im Portal standardmäßig ausgewählt."
                    rightSection={
                      <Switch
                        size="sm"
                        aria-label="Option vorausgewählt"
                        checked={autoSelect}
                        onChange={() => onChange({ autoSelect: !autoSelect })}
                      />
                    }
                  />
                )}
                {showNegotiableControl && (
                  <>
                    <ConfigOption
                      title="Kunde kann Menge wählen"
                      description="Kunden können die Menge im Portal selbst festlegen. Mindest- und Maximalmenge begrenzen den erlaubten Bereich."
                      rightSection={
                        <Switch
                          size="sm"
                          aria-label="Kunde kann Menge wählen"
                          checked={negotiable}
                          onChange={() => onChange({ negotiable: !negotiable })}
                        />
                      }
                    />
                    {negotiable && (
                      <Group spacing={4} align="flex-end" position="right">
                        <NumberInput
                          hideControls
                          label={
                            <Text color="dimmed" size="xs">
                              Mindestmenge
                            </Text>
                          }
                          min={0}
                          precision={0}
                          placeholder="z.B. 1"
                          defaultValue={min}
                          sx={{ width: 100 }}
                          onKeyDown={handleIntegerKeyDown}
                          onPaste={handleIntegerPaste}
                          onBlur={(event) => onChange({ min: Number(event.currentTarget.value) || 0 })}
                        />
                        <NumberInput
                          hideControls
                          label={
                            <Text color="dimmed" size="xs">
                              Maximalmenge
                            </Text>
                          }
                          min={0}
                          precision={0}
                          placeholder="Kein Limit"
                          defaultValue={max === 0 ? undefined : max}
                          sx={{ width: 100 }}
                          onKeyDown={handleIntegerKeyDown}
                          onPaste={handleIntegerPaste}
                          onBlur={(event) => onChange({ max: Number(event.currentTarget.value) || 0 })}
                        />
                      </Group>
                    )}
                  </>
                )}
              </Stack>
            )}
            <Group position="center" py="xs">
              <LinkText size="xs" onClick={() => setShowMoreSettings((show) => !show)}>
                {showMoreSettings ? 'Weniger Einstellungen anzeigen' : 'Weitere Einstellungen anzeigen'}
              </LinkText>
            </Group>
          </>
        )}
      </Stack>
    </>
  );
};

export default PositionSettings;
